/**
 * BondSeriesBuilder - Validation and processing logic for bond series
 *
 * Handles validation of bond series input parameters and builds summaries
 * for display and analysis purposes.
 */

import { InterestCalculationEngine } from '~/engine/processing/engines/interest-engine/interest-calculator';
import type { BondPaymentDateMap, BondSeries, DateRecordsMapByPaymentDate, Project } from '~/types';
import { constructPaymentDateKey } from '../project-dates/ProjectDatesHelper';

export class BondSeriesBuilder {
  /**
   * Validates bond series input parameters
   */
  static validateInputs(inputs: BondSeries): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Basic field validation
    if (!inputs.name || inputs.name.trim() === '') {
      errors.push('Bond name is required');
    }

    if (!inputs.issueDate) {
      errors.push('Issue date is required');
    } else {
      // Validate date format (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(inputs.issueDate)) {
        errors.push('Issue date must be in YYYY-MM-DD format');
      } else {
        const issueDate = new Date(inputs.issueDate);
        if (Number.isNaN(issueDate.getTime())) {
          errors.push('Issue date must be a valid date');
        }
      }
    }

    // Interest payment validation
    if (!inputs.interestPaymentMonths || inputs.interestPaymentMonths.length === 0) {
      errors.push('At least one interest payment month is required');
    } else {
      // Validate month values (1-12)
      const invalidMonths = inputs.interestPaymentMonths.filter((month) => month < 1 || month > 12);
      if (invalidMonths.length > 0) {
        errors.push(`Invalid interest payment months: ${invalidMonths.join(', ')}`);
      }
    }

    if (!inputs.firstInterestPaymentDate) {
      errors.push('First interest payment date is required');
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(inputs.firstInterestPaymentDate)) {
        errors.push('First interest payment date must be in YYYY-MM-DD format');
      } else {
        const firstInterestDate = new Date(inputs.firstInterestPaymentDate);
        if (Number.isNaN(firstInterestDate.getTime())) {
          errors.push('First interest payment date must be a valid date');
        }
      }
    }

    // Principal payment validation
    if (!inputs.principalPaymentMonths || inputs.principalPaymentMonths.length === 0) {
      errors.push('At least one principal payment month is required');
    } else {
      // Validate month values (1-12)
      const invalidMonths = inputs.principalPaymentMonths.filter(
        (month) => month < 1 || month > 12
      );
      if (invalidMonths.length > 0) {
        errors.push(`Invalid principal payment months: ${invalidMonths.join(', ')}`);
      }
    }

    if (!inputs.firstPrincipalPaymentDate) {
      errors.push('First principal payment date is required');
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(inputs.firstPrincipalPaymentDate)) {
        errors.push('First principal payment date must be in YYYY-MM-DD format');
      } else {
        const firstPrincipalDate = new Date(inputs.firstPrincipalPaymentDate);
        if (Number.isNaN(firstPrincipalDate.getTime())) {
          errors.push('First principal payment date must be a valid date');
        }
      }
    }

    if (!inputs.finalPrincipalPaymentDate) {
      errors.push('Final principal payment date is required');
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(inputs.finalPrincipalPaymentDate)) {
        errors.push('Final principal payment date must be in YYYY-MM-DD format');
      } else {
        const finalPrincipalDate = new Date(inputs.finalPrincipalPaymentDate);
        if (Number.isNaN(finalPrincipalDate.getTime())) {
          errors.push('Final principal payment date must be a valid date');
        }
      }
    }

    // Cross-field validation
    if (inputs.issueDate && inputs.firstInterestPaymentDate) {
      const issueDate = new Date(inputs.issueDate);
      const firstInterestDate = new Date(inputs.firstInterestPaymentDate);

      if (firstInterestDate <= issueDate) {
        errors.push('First interest payment date must be after issue date');
      }
    }

    if (inputs.issueDate && inputs.firstPrincipalPaymentDate) {
      const issueDate = new Date(inputs.issueDate);
      const firstPrincipalDate = new Date(inputs.firstPrincipalPaymentDate);

      if (firstPrincipalDate <= issueDate) {
        errors.push('First principal payment date must be after issue date');
      }
    }

    if (inputs.firstPrincipalPaymentDate && inputs.finalPrincipalPaymentDate) {
      const firstPrincipalDate = new Date(inputs.firstPrincipalPaymentDate);
      const finalPrincipalDate = new Date(inputs.finalPrincipalPaymentDate);

      if (finalPrincipalDate <= firstPrincipalDate) {
        errors.push('Final principal payment date must be after first principal payment date');
      }
    }

    // Interest rate validation - require __interestRateEscalation
    const bondWithEscalation = inputs as BondSeries & {
      __interestRateEscalation?: {
        startRate: number;
        incrementRate: number;
        maxRate: number;
      };
    };

    if (!bondWithEscalation.__interestRateEscalation) {
      errors.push('Bond must have __interestRateEscalation configured');
    } else {
      const escalation = bondWithEscalation.__interestRateEscalation;

      if (escalation.startRate < 0 || escalation.startRate > 1) {
        errors.push('Starting interest rate must be between 0 and 1 (0% to 100%)');
      }

      if (escalation.maxRate < 0 || escalation.maxRate > 1) {
        errors.push('Maximum interest rate must be between 0 and 1 (0% to 100%)');
      }

      if (escalation.incrementRate < 0 || escalation.incrementRate > 0.1) {
        errors.push('Interest rate increment must be between 0 and 0.1 (0% to 10%)');
      }

      if (escalation.startRate > escalation.maxRate) {
        errors.push('Starting interest rate cannot be greater than maximum interest rate');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Builds a summary of the bond series for display
   */
  static buildSummary(inputs: BondSeries) {
    const issueDate = new Date(inputs.issueDate);
    const finalPrincipalDate = new Date(inputs.finalPrincipalPaymentDate);

    // Calculate bond term in years
    const bondTermYears = Math.ceil(
      (finalPrincipalDate.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25)
    );

    // Format payment months
    const interestMonths = inputs.interestPaymentMonths
      .map((month) => new Date(2000, month - 1, 1).toLocaleString('default', { month: 'short' }))
      .join(', ');

    const principalMonths = inputs.principalPaymentMonths
      .map((month) => new Date(2000, month - 1, 1).toLocaleString('default', { month: 'short' }))
      .join(', ');

    return {
      bondTermYears,
      interestPaymentFrequency: inputs.interestPaymentMonths.length,
      principalPaymentFrequency: inputs.principalPaymentMonths.length,
      interestMonths,
      principalMonths,
      totalPayments: {
        interest: bondTermYears * inputs.interestPaymentMonths.length,
        principal: bondTermYears * inputs.principalPaymentMonths.length,
      },
    };
  }

  /**
   * Builds the bond payment date map with escalating interest rates and efficiency fields
   * This populates the bondPaymentMap with payment dates, interest rates, and pre-calculated day counts
   */
  static buildBondPaymentMap(
    bondSeries: BondSeries,
    dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate
  ): BondPaymentDateMap {
    const bondPaymentMap: BondPaymentDateMap = {};

    // Validate that the first interest payment date exists in the date records
    const firstInterestPaymentDateKey = constructPaymentDateKey(
      bondSeries.firstInterestPaymentDate
    );

    const firstDateRecord = dateRecordsMapByPaymentDate[firstInterestPaymentDateKey];

    if (!firstDateRecord) {
      throw new Error(
        `No date record found for first interest payment date: ${bondSeries.firstInterestPaymentDate}`
      );
    }

    // First pass: Create all entries with basic information
    const paymentKeys: number[] = [];
    for (const paymentDateKeyStr in dateRecordsMapByPaymentDate) {
      const paymentDateKey = parseInt(paymentDateKeyStr, 10);
      const dateRecord = dateRecordsMapByPaymentDate[paymentDateKey];
      const paymentDate = dateRecord.paymentDate;

      // Check if this payment date is within the bond's active period
      if (
        paymentDate >= bondSeries.firstInterestPaymentDate &&
        paymentDate <= bondSeries.finalPrincipalPaymentDate
      ) {
        // Create the bond payment date map entry with placeholder interest rate
        // We'll calculate the actual rate after sorting to get proper payment indices
        bondPaymentMap[paymentDateKey] = {
          paymentDate: dateRecord.paymentDate,
          taxYear: dateRecord.taxYear,
          fiscalYearEndDate: dateRecord.fiscalYearEndDate,
          isInterestOnly: dateRecord.isInterestOnly,
          isLastPaymentInFiscalYear: dateRecord.isLastPaymentInFiscalYear,
          interestRate: 0, // Placeholder - will be calculated after sorting
        };

        paymentKeys.push(paymentDateKey);
      }
    }

    // Sort payment keys to ensure chronological order
    paymentKeys.sort((a, b) => a - b);

    // Second pass: Calculate interest rates with per-payment escalation
    // Get escalation settings from bond (required - set by factory)
    interface InterestRateEscalation {
      startRate: number;
      incrementRate: number;
      maxRate: number;
    }

    const bondSeriesWithEscalation = bondSeries as BondSeries & {
      __interestRateEscalation?: InterestRateEscalation;
    };

    if (!bondSeriesWithEscalation.__interestRateEscalation) {
      throw new Error(
        `Bond ${bondSeries.name} is missing __interestRateEscalation. ` +
          `Bonds must be created via createBondFromQuickInput() or createBondFromConfiguration().`
      );
    }

    const escalation: InterestRateEscalation = bondSeriesWithEscalation.__interestRateEscalation;

    let currentRate = escalation.startRate;

    for (let i = 0; i < paymentKeys.length; i++) {
      const currentKey = paymentKeys[i];
      const currentEntry = bondPaymentMap[currentKey];

      // Calculate escalating rate
      const paymentRate = Math.min(currentRate, escalation.maxRate);

      // Update the interest rate with proper rounding
      currentEntry.interestRate = Math.round(paymentRate * 10000) / 10000; // Round to 4 decimal places

      // Increment rate for next payment
      currentRate += escalation.incrementRate;
    }

    // Third pass: Calculate day counts between payments
    for (let i = 0; i < paymentKeys.length; i++) {
      const currentKey = paymentKeys[i];
      const currentEntry = bondPaymentMap[currentKey];

      // Calculate days from prior payment
      if (i > 0) {
        const priorKey = paymentKeys[i - 1];
        const priorEntry = bondPaymentMap[priorKey];
        currentEntry.daysToPrior = InterestCalculationEngine.days360(
          priorEntry.paymentDate,
          currentEntry.paymentDate
        );
      } else {
        // First payment - calculate from issue date
        currentEntry.daysToPrior = InterestCalculationEngine.days360(
          bondSeries.issueDate,
          currentEntry.paymentDate
        );
      }

      // Calculate days to next payment
      if (i < paymentKeys.length - 1) {
        const nextKey = paymentKeys[i + 1];
        const nextEntry = bondPaymentMap[nextKey];
        currentEntry.daysToNext = InterestCalculationEngine.days360(
          currentEntry.paymentDate,
          nextEntry.paymentDate
        );
      }
      // Note: Last payment won't have daysToNext, which is intentional
    }

    return bondPaymentMap;
  }

  /**
   * Initialize a bond series with its payment map
   * This should be called after a bond series is created to populate the payment schedule
   */
  static initializeBondSeries(bondSeries: BondSeries, project: Project): BondSeries {
    // Ensure we have the date records map
    if (!project.dateElements?.dateRecordsMapByPaymentDate) {
      throw new Error('Project must have dateRecordsMapByPaymentDate initialized');
    }

    // Build the bond payment map
    const bondPaymentMap = BondSeriesBuilder.buildBondPaymentMap(
      bondSeries,
      project.dateElements.dateRecordsMapByPaymentDate
    );

    // Return a new bond series with the payment map populated
    return {
      ...bondSeries,
      bondPaymentMap,
    };
  }

  /**
   * Initialize bond payment map on-demand if it doesn't exist
   * Useful for demo/viewer components that need to display payment schedules
   */
  static ensureBondPaymentMapInitialized(bondSeries: BondSeries, project: Project): BondSeries {
    // If payment map is already populated, return as-is
    if (bondSeries.bondPaymentMap && Object.keys(bondSeries.bondPaymentMap).length > 0) {
      return bondSeries;
    }

    // Try to initialize the payment map
    try {
      return BondSeriesBuilder.initializeBondSeries(bondSeries, project);
    } catch (error) {
      console.warn(`Cannot initialize bond payment map for ${bondSeries.name}:`, error);
      return bondSeries;
    }
  }
}
